import React, { useState, useRef, useEffect, memo } from 'react';

const SearchableSelect = memo(({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  loading = false,
  error = null,
  className = "",
  disabled = false,
  showSearchBar = true,
  searchTerm: externalSearchTerm,
  onSearchChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Usar searchTerm externo si se proporciona, sino usar el interno
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const setSearchTerm = onSearchChange || setInternalSearchTerm;

  // Filtrar opciones basado en el término de búsqueda
  useEffect(() => {
    // Si se proporciona searchTerm externo, usar las opciones tal como vienen (ya filtradas)
    if (externalSearchTerm !== undefined) {
      setFilteredOptions(options);
    } else {
      // Lógica interna de filtrado
      if (!searchTerm.trim()) {
        setFilteredOptions(options); // Mostrar todas las opciones disponibles
      } else {
        const filtered = options.filter(option => {
          // Manejar tanto Make_Name como Model_Name
          const name = option.Make_Name || option.Model_Name || '';
          return name.toLowerCase().includes(searchTerm.toLowerCase());
        });
        setFilteredOptions(filtered);
      }
    }
  }, [options, searchTerm, externalSearchTerm]);

  // Cerrar dropdown cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleSelect = (option) => {
    const optionName = option.Model_Name || option.Make_Name;
    onChange(optionName);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const selectedOption = options.find(option => 
    option.Model_Name === value || option.Make_Name === value
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Barra de búsqueda siempre visible cuando está abierto */}
      {isOpen && showSearchBar && (
        <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
               placeholder="Type to search..."
              className="flex-1 p-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              autoFocus
            />
            <button
              onClick={() => {
                setSearchTerm('');
                setIsOpen(false);
              }}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Input/Button que muestra el valor seleccionado */}
      <div
        onClick={handleToggle}
        className={`w-full p-3 border border-gray-300 rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 bg-white shadow-sm cursor-pointer flex items-center justify-between ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption ? (selectedOption.Model_Name || selectedOption.Make_Name) : placeholder}
        </span>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Campo de búsqueda dentro del dropdown (backup) */}
          {!showSearchBar && (
            <div className="p-2 border-b border-gray-200">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search makes..."
                className="w-full p-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              />
            </div>
          )}

          {/* Lista de opciones */}
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-center text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mx-auto mb-2"></div>
                Loading makes...
              </div>
            ) : error ? (
              <div className="p-3 text-center text-red-500">
                Error loading makes
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="p-3 text-center text-gray-500">
                {searchTerm ? 'No makes found' : 'No makes available'}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const optionName = option.Model_Name || option.Make_Name;
                const optionId = option.Model_ID || option.Make_ID;
                return (
                  <div
                    key={optionId}
                    onClick={() => handleSelect(option)}
                    className={`p-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150 ${
                      value === optionName ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                    }`}
                  >
                    {optionName}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer con información */}
          {!loading && !error && (
            <div className="p-2 text-xs text-gray-500 border-t border-gray-200 bg-gray-50">
              {searchTerm ? 
                `Showing ${filteredOptions.length} of ${options.length} options` :
                `Showing ${options.length} options`
              }
            </div>
          )}
        </div>
      )}
    </div>
  );
});

SearchableSelect.displayName = 'SearchableSelect';

export default SearchableSelect;
